import * as Sentry from '@sentry/react';
import { post } from 'src/api/api';
import { isDevelopment } from 'src/utils';
import {
  MobileOperatingSystem,
  getMobileOperatingSystem,
} from 'src/utils/get-mobile-operating-system';

import {
  ANALYTICS_ENDPOINT,
  ANALYTICS_ENDPOINT_V2,
} from '@features/Analytics/constants';
import { createTestSegmentFromQueryParameters } from '@features/Analytics/utils';

import { AbTestsResponseError, readAbTestsResponse } from './ab-tests-response';

export const sendAnalyticsId = async ({
  googlePseudoId,
  userPseudoIdApplePay,
  userPseudoIdWinback,
  mg,
}: {
  googlePseudoId?: string;
  userPseudoIdApplePay?: string;
  userPseudoIdWinback?: string;
  mg: number;
}) =>
  await post({
    endpoint: isDevelopment
      ? 'https://dev.analytics.mentalgrowth.app/api/v1'
      : ANALYTICS_ENDPOINT_V2,
    path: 'id_aggregator',
    body: {
      user_pseudo_id: googlePseudoId,
      origin: 'mimika',
      mg: mg,
      user_pseudo_id_apple_pay: userPseudoIdApplePay,
      user_pseudo_id_winback: userPseudoIdWinback,
    },
  });

export const getAbTest = async ({
  name,
  userId,
  abortSignal,
}: {
  name: string;
  userId?: number | null;
  abortSignal?: AbortSignal;
}) =>
  await post({
    endpoint: isDevelopment
      ? `${typeof window !== 'undefined' ? window.location.origin : ''}/api/analytics/`
      : ANALYTICS_ENDPOINT,
    path: 'single_web_ab_test',
    body: {
      test_name: name,
      server_id: userId,
      env_type:
        process.env.NEXT_PUBLIC_ENV === 'development' ? 'test' : 'production',
    },
    abortSignal,
  });

export const getAbTests = async ({
  variant,
  utm_source,
  utm_campaign,
  region,
  user_id,
  abortSignal,
}: {
  variant: string | undefined;
  utm_source: string | undefined;
  utm_campaign: string | undefined;
  region: string | undefined;
  user_id: string | undefined;
  abortSignal?: AbortSignal;
}) => {
  const isAndroidDevice =
    getMobileOperatingSystem() === MobileOperatingSystem.ANDROID
      ? 'android'
      : '';
  const isIosDevise =
    getMobileOperatingSystem() === MobileOperatingSystem.IOS ? 'ios' : '';
  const basePath = 'ab_tests';
  const fullUrl = `${basePath}?app=mimika&build=${
    isDevelopment ? 'test' : 'prod'
  }&os=${
    isAndroidDevice || isIosDevise
  }&variant=${variant}&utm_source=${utm_source}&utm_campaign=${utm_campaign}&region=${region}&user_id=${user_id}`;

  const pseudoData = createTestSegmentFromQueryParameters();

  if (pseudoData != null) return pseudoData;

  const url = new URL(fullUrl, ANALYTICS_ENDPOINT_V2).href;
  const response = await fetch(url, {
    credentials: 'include',
    signal: abortSignal,
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });

  try {
    return await readAbTestsResponse(response, url);
  } catch (error) {
    if (error instanceof AbTestsResponseError) {
      Sentry.addBreadcrumb({
        category: 'ab-tests.response',
        level: 'warning',
        message: error.message,
        data: { reason: error.reason, ...error.details },
      });
    }
    throw error;
  }
};
